import { safeExportUrl } from "./notebookExport"

async function decodeImage(image: HTMLImageElement, signal: AbortSignal) {
  signal.throwIfAborted()
  let abort: () => void = () => {}
  try {
    await Promise.race([
      image.decode(),
      new Promise<never>((_resolve, reject) => {
        abort = () =>
          reject(new DOMException("Image loading canceled", "AbortError"))
        signal.addEventListener("abort", abort, { once: true })
      }),
    ])
  } finally {
    signal.removeEventListener("abort", abort)
  }
}

async function imageData(src: string, signal: AbortSignal) {
  // Load under img-src, not connect-src. Cross-origin hosts still need to allow canvas access.
  const image = new Image()
  image.crossOrigin = "anonymous"
  image.referrerPolicy = "no-referrer"
  image.src = src
  try {
    await decodeImage(image, signal)
    signal.throwIfAborted()
    const canvas = document.createElement("canvas")
    const scale = Math.min(
      1,
      4096 / image.naturalWidth,
      4096 / image.naturalHeight,
    )
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
    const context = canvas.getContext("2d")
    if (!context) {
      throw new Error("Image capture unavailable")
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL("image/png")
  } finally {
    image.src = ""
  }
}

/** Only the off-screen export copies are changed; inaccessible artwork remains explicit in the PDF. */
export async function prepareNotebookSlideImages(
  slides: HTMLElement[],
  signal: AbortSignal,
) {
  return Promise.all(
    slides.map(async (slide) => {
      const warnings = await Promise.all(
        [...slide.querySelectorAll("img")].map(async (image) => {
          signal.throwIfAborted()
          const src = image.currentSrc || image.src
          const label = image.alt || "Illustration"
          const imageSignal = AbortSignal.any([
            signal,
            AbortSignal.timeout(10_000),
          ])
          try {
            image.loading = "eager"
            if (!src.startsWith("data:")) {
              const data = await imageData(src, imageSignal)
              imageSignal.throwIfAborted()
              image.removeAttribute("srcset")
              image.src = data
            }
            await decodeImage(image, imageSignal)
            signal.throwIfAborted()
            return null
          } catch {
            signal.throwIfAborted()
            const placeholder = document.createElement("p")
            placeholder.textContent = `Image not embedded in PDF: ${label}. `
            const url = safeExportUrl(src, window.location.origin)
            if (url) {
              const link = document.createElement("a")
              link.href = url
              link.textContent = "Open original image"
              placeholder.append(link)
            }
            image.replaceWith(placeholder)
            return label
          }
        }),
      )
      return warnings.filter((warning): warning is string => warning !== null)
    }),
  )
}
